// Ensure dynamically imported chunks resolve relative to index.html under file:// protocol
// eslint-disable-next-line no-undef
if (typeof window !== 'undefined' && window.location && window.location.protocol === 'file:') { __webpack_public_path__ = './'; }

import Vue from 'vue'
import App from './App.vue'
import './registerServiceWorker'
import router from './router'
import store from './store'
import * as Sentry from '@sentry/vue'
import { startBatchTimer } from './composables/useAppUsage';
import 'bootstrap/dist/js/bootstrap.min'
import 'bootstrap/dist/css/bootstrap.min.css';
import { App as CapApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import './assets/fonts/icons.js'
import AndroidSyncCoordinator from './services/AndroidSyncCoordinator'
import DrawerLayout from 'vue-drawer-layout'
import './services/GlobalVariables'
import './services/ProxyHandler'
// Waiter-safe table/sale API errors globally (never "Table Does not Exist").
// Founder + Victoria brief 2026-08-09 — Joseph WaiterStation.
// 2.1.14 — TicketApp #26827 offline line voids via voidOutbox.
import axios from 'axios'
import { errorHandler as lytningErrorHandler } from './services/errorHandler'
lytningErrorHandler.installTableErrorInterceptor(axios)
import VueVirtualScroller from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import VueToggles from 'vue-toggles';
import Loading from 'vue-loading-overlay';
import vSelect from 'vue-select'
import { VueEditor } from "vue2-editor";
import "vue-select/dist/vue-select.css";
import './assets/styles/global.css';
// Import stylesheet

import 'vue-loading-overlay/dist/vue-loading.css';
import VueBarcodeScanner from 'vue-barcode-scanner'
import { migrateLegacyVuexState } from './services/migrateOldState';
import { getDB } from './services/Storage';
// AB#25724: LAN kitchen print on Main — register IPC listener ASAP (no-op if not Electron)
import { registerTableServerPrintListener } from './services/tableServerPrintListener';

// Register table-server kitchen print listener early so Main is ready before tablet prints
registerTableServerPrintListener();

let options = {
    sensitivity: 300, // default is 100
    requiredAttr: true, // default is false
    controlSequenceKeys: ['NumLock', 'Clear'], // default is null
    callbackAfterTimeout: true // default is false
}
import VueGoodTablePlugin from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css'
import VueCompositionAPI from "@vue/composition-api";
import { installGlobalUUID } from '@/utils/uuid';

import BootstrapVue from 'bootstrap-vue';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
Vue.use(BootstrapVue);

Vue.use(VueCompositionAPI);
Vue.use(VueGoodTablePlugin);
Vue.use(VueBarcodeScanner, options);

// Expose SweetAlert2 on the Vue prototype as `$swal` so components that
// follow the `vue-sweetalert2` convention (`this.$swal.fire(...)`)
// work without each component importing `Swal` directly. Stock-transfer
// components (SendingItemList, SendingItemRow, ViewIncomingTransfer,
// SearchItemsToSend) rely on this. Must be set BEFORE `new Vue(...)` is
// instantiated below, otherwise `$swal` is `undefined` in those
// components and the click handler throws
// `TypeError: Cannot read properties of undefined (reading 'fire')`.
import Swal from 'sweetalert2';
Vue.prototype.$swal = Swal;

Vue.component('loading', Loading);
Vue.component('VueToggles', VueToggles);
Vue.use(VueVirtualScroller)
Vue.use(DrawerLayout);
Vue.component('vue-editor', VueEditor)
Vue.component('v-select', vSelect)
Vue.config.productionTip = false;
Vue.config.devtools = false;

/**
 * Strips known PII keys from event bodies before they leave the device.
 * Defence-in-depth on top of `dataCollection.userInfo: false` so a future
 * config change cannot accidentally leak credentials to Sentry. Restaurant
 * POS handles PIN-based auth and deeplink sessions, so PIN/password/session
 * keys must never leave the renderer.
 * @param {import("@sentry/vue").Event} event - the Sentry event
 * @returns {import("@sentry/vue").Event | null} redacted event, or null to drop
 */
function redactPII(event) {
  if (!event) return event;
  const dropKeys = ["pin", "password", "creditCard", "credit_card", "token", "authorization", "sessionId", "sessionExpiresAt"];
  const scrub = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    for (const k of Object.keys(obj)) {
      if (dropKeys.includes(k.toLowerCase())) {
        obj[k] = "[REDACTED]";
      } else if (typeof obj[k] === "object") {
        scrub(obj[k]);
      }
    }
    return obj;
  };
  return scrub(event);
}

/**
 * Sentry Vue 2 renderer init.
 *
 * Renderer-only init: covers the web SPA, Electron renderer, and Capacitor
 * WebView. The Electron main process and Capacitor native side are out of
 * scope for this rollout and will be picked up by a follow-up plan.
 *
 * Order matters: `Sentry.init` must run AFTER the router instance is
 * constructed so `browserTracingIntegration({ router })` can attach to it,
 * but BEFORE `new Vue().$mount('#app')` so the Vue error handler is wired.
 */
Sentry.init({
  Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  // Performance sampling: 100% in dev, lower in prod. Tied to the environment
  // file loaded at build time.
  tracesSampleRate: 1.0,
  // Replay sampling: 10% baseline sessions, 100% when an error occurs.
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Forward console logs to Sentry so we get breadcrumbs from the offline
  // SQLite / STOMP flows which already log heavily.
  enableLogs: true,
  release: process.env.VUE_APP_SENTRY_RELEASE,
  environment: process.env.VUE_APP_SENTRY_ENVIRONMENT || "production",
  // Opt out of automatic user-info collection. PII fields (email) are still
  // scrubbed by `redactPII` if they sneak through via request bodies.
  dataCollection: {
    userInfo: false,
  },
  beforeSend: redactPII,
});





(async () => {
    // Initialize storage adapter first (SQLite for Electron, IndexedDB for web/mobile)
    try {
        // Initialize storage adapter first (SQLite for Electron, IndexedDB for web/mobile)
        try {
            //console.log('[App] Initializing storage adapter...');
            const { getStorage } = await import('./services/storageAdapter');
            const storage = await getStorage();
            ////console.log('[App] Storage adapter initialized successfully');
        } catch (err) {
            console.error('[App] Failed to initialize storage adapter:', err);
            // Continue anyway - individual services will handle their own initialization
        }

        await migrateLegacyVuexState();

        // Initialize theme via Vuex (respect saved preference; avoid direct DOM)
        try {
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme === 'light' || savedTheme === 'dark') {
                store.commit('setTheme', savedTheme);
            } else if (typeof window !== 'undefined' && window.matchMedia) {
                const mq = window.matchMedia('(prefers-color-scheme: dark)');
                store.commit('setTheme', mq.matches ? 'dark' : 'light');
            } else {
                store.commit('setTheme', 'light');
            }
        } catch (_) {
            store.commit('setTheme', 'light');
        }

        // Force reduced motion unconditionally for maximum performance (no detection)
        try {
            document.body.classList.add('reduced-motion');
        } catch (e) {
            // Safety: never break boot due to DOM access timing
            console.warn('[App] Failed to apply reduced-motion class:', e);
        }

        new Vue({
            router,
            store,
            render: h => h(App),
            async mounted() {


                // Electron: get and log available printers (no-op on web/mobile)
                try {
                    if (window && window.electronBridge && typeof window.electronBridge.getPrinters === 'function') {
                        const printers = await window.electronBridge.getPrinters();
                        ////console.log('Available printers:', printers);
                    }
                } catch (e) {
                    console.error('Failed to fetch printers:', e);
                }


                // Install UUID generator globally
                installGlobalUUID();

                // Hydration is handled by vuex-persist in idbStorage.js
                // Sync service is started in App.vue after hydration completes

                // Install Android Sync Coordinator (foreground/resume-based)
                try {
                    AndroidSyncCoordinator.install();
                } catch (e) {
                    console.warn('[App] Failed to install AndroidSyncCoordinator:', e);
                }

                // Android hardware back button handling (Capacitor)
                try {
                    const isNative = Capacitor?.isNativePlatform?.() === true;
                    const platform = Capacitor?.getPlatform?.() || 'web';
                    if (isNative && platform === 'android' && CapApp?.addListener) {
                        let lastBackTime = 0;
                        CapApp.addListener('backButton', ({ canGoBack }) => {
                            // If Vue Router has history or Capacitor reports canGoBack, navigate back
                            const hasHistory = window.history.length > 1;
                            const routeName = this.$route?.name;

                            // Define routes that are considered roots of the app
                            const rootRoutes = new Set(['pos', 'poslogin', 'viewSale', 'payment', 'viewPayment', 'float']);

                            if (hasHistory || canGoBack) {
                                // Special case: if current view handles its own back (e.g., modals), emit a global event first
                                window.dispatchEvent(new CustomEvent('androidBackButton'));
                                // Navigate back after a tiny delay to allow handlers to intercept
                                setTimeout(() => {
                                    this.$router.back();
                                }, 0);
                                return;
                            }

                            // If on a root route or no history, minimize app instead of exiting
                            if (rootRoutes.has(routeName)) {
                                CapApp.minimizeApp?.();
                                return;
                            }

                            // Default fallback: if we somehow reached here without history, minimize
                            CapApp.minimizeApp?.();
                        });

                        if (process.env.NODE_ENV !== 'production') {
                            //console.log('[App] Android back button handler installed');
                        }
                    }
                } catch (e) {
                    console.warn('[App] Failed to set up Android back button handler:', e);
                }

                // Start App Usage tracking batch timer (auto-flush every 30 seconds)
                try {
                    startBatchTimer()
                } catch (e) {
                    console.warn('[App] Failed to start App Usage tracking:', e);
                }
            }
        }).$mount('#app')

        if (navigator.storage && navigator.storage.persist) {
            const isPersistent = await navigator.storage.persisted();

            if (!isPersistent) {
                const granted = await navigator.storage.persist();

            }
        }
    } catch (error) {
        console.error('[App] Initialization error:', error);
    }
})();
